import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell,
} from 'recharts';
import './emotionchart.css';

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowWidth;
};

const LABEL_MAP = { happy: 'Happy', calm: 'Neutral', joy: 'Surprise', sad: 'Least Interested', 'very sad': 'Not Interested' };
const COLOR_MAP = { Happy: '#96e6b3', Neutral: '#a4d8c2', Surprise: '#0b3c6b', 'Least Interested': '#8884d8', 'Not Interested': '#b0c4de' };

const transformData = (emotionData) => {
  if (!emotionData) return [];
  const labelOrder = ['Happy', 'Neutral', 'Surprise', 'Least Interested', 'Not Interested'];
  return Object.entries(emotionData)
    .map(([originalName, value]) => {
      const newName = LABEL_MAP[originalName];
      return newName ? { name: newName, value: parseFloat(value) || 0, color: COLOR_MAP[newName], full: 100 } : null;
    })
    .filter(Boolean)
    .sort((a, b) => labelOrder.indexOf(a.name) - labelOrder.indexOf(b.name));
};

const CustomBarLabel = ({ x, y, width, value, index, activeIndex, isMobile }) => {
  if (index !== activeIndex) return null;

  const paddingvalue = isMobile ? 5 : 5;
  const textWidth = String(value).length * 8 + paddingvalue;
  const halfTextWidth = textWidth / 2;

  const pathData = `M0 0L-7 -7L-${halfTextWidth} -7A15 15 0 0 1-${halfTextWidth} -37L${halfTextWidth} -37A15 15 0 0 1 ${halfTextWidth} -7L7 -7Z`;

  return (
    <g transform={`translate(${x + width / 2}, ${y - 8})`}>
      <path d={pathData} fill="#000" />
      <text x={0} y={-22} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="14px" fontWeight="bold" fontFamily="Archivo">
        {`${value}%`}
      </text>
    </g>
  );
};

const CustomXAxisTick = React.memo(({ x, y, payload, isMobile }) => (
  <g transform={`translate(${x},${y})`}>
    {payload.value.split(' ').map((word, index) => (
      <text
        key={index}
        x={-10}
        y={15}
        dy={index * 12}
        textAnchor="middle"
        fontSize={isMobile ? 8 : 12}
        fill="#00000066"
      >
        {word}
      </text>
    ))}
  </g>
));

const EmotionalStateChart = ({ emotionData }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const data = useMemo(() => transformData(emotionData), [emotionData]);
  const windowWidth = useWindowWidth();

  const handleMouseEnter = (_, index) => setActiveIndex(index);
  const handleMouseLeave = () => setActiveIndex(null);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const chartMargin = {
    top: isMobile ? 5 : isTablet ? 20 : 15,
    right: 15, left: -15,
    bottom: isMobile ? -10 : isTablet ? -10 : 5,
  };

  const baseStyle = { transition: 'all 0.15s ease-in-out ' };
  const activeStyle = {
    ...baseStyle,
    transform: 'translateY(-6px)',
    filter: 'drop-shadow(4px 6px 4px rgba(0,0,0,0.5))',
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        margin={chartMargin}
        barCategoryGap={isMobile ? "40%" : "80%"} // increase spacing between bars
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          interval={0}
          height={50}
          tick={<CustomXAxisTick isMobile={isMobile} windowWidth={windowWidth} totalLabels={data.length} />}
        />

        <YAxis
          axisLine={false} tickLine={false} tickFormatter={(tick) => `${tick}%`}
          domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontSize: 12 }}
          tickMargin={isMobile ? 8 : 5}
        />

        <Bar
          dataKey="value" radius={isMobile ? [5, 5, 5, 5] : [8, 8, 8, 8]}
          label={<CustomBarLabel activeIndex={activeIndex} isMobile={isMobile} />} barSize={28} isAnimationActive={false}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} style={index === activeIndex ? activeStyle : baseStyle} />
          ))}
        </Bar>

        <Bar dataKey="value" barSize={28} fill="transparent" isAnimationActive={false}>
          {data.map((entry, index) => (
            <Cell
              key={`event-cell-${index}`}
              cursor="pointer"
              onMouseEnter={() => handleMouseEnter(entry, index)}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default EmotionalStateChart;